import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "./common";
import {
  BrowserLink,
  BrowserData,
  BrowserRes,
  BROWSER_REL,
} from "../../pages/identity/create/browserServiceModal/types";

export const browserApi = createApi({
  reducerPath: "browserApi",
  baseQuery: customBaseQuery,
  tagTypes: ["BrowserServices", "BrowserRegions", "BrowserTypes", "BrowserItems", "BrowserChildren"],
  endpoints: (builder) => ({
    getServices: builder.query<BrowserLink[], string>({
      queryFn: async (cloudaccountLink) => {
        try {
          const { request } = await import("../../services/cpln");
          let services: BrowserLink[] = [];
          let nextLink: string | undefined = `/browse${cloudaccountLink}`;

          while (nextLink) {
            const { data } = await request<BrowserRes>({
              service: "browser",
              url: nextLink,
            });
            const pageServices = data.links
              .filter((l) => l.rel === BROWSER_REL.service)
              .map((s) => ({
                _type: "link" as const,
                rel: s.rel,
                label: s.label || "",
                href: s.href,
              }));
            services = services.concat(pageServices);
            nextLink = data.links.find((l) => l.rel === BROWSER_REL.next)?.href;
          }

          return { data: services };
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status || 500,
              data: error.response?.data || error.message,
            },
          };
        }
      },
      providesTags: (result, error, cloudaccountLink) => [
        { type: "BrowserServices", id: cloudaccountLink },
      ],
    }),

    getRegions: builder.query<BrowserLink[], string>({
      queryFn: async (serviceHref) => {
        try {
          const { request } = await import("../../services/cpln");
          let regions: BrowserLink[] = [];
          let nextLink: string | undefined = serviceHref;

          while (nextLink) {
            const { data } = await request<BrowserRes>({
              service: "browser",
              url: nextLink,
            });
            const pageRegions = data.links
              .filter((l) => l.rel === BROWSER_REL.region)
              .map((r) => ({
                _type: "link" as const,
                rel: r.rel,
                label: r.label || "",
                href: r.href,
              }));
            regions = regions.concat(pageRegions);
            nextLink = data.links.find((l) => l.rel === BROWSER_REL.next)?.href;
          }

          return { data: regions };
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status || 500,
              data: error.response?.data || error.message,
            },
          };
        }
      },
      providesTags: (result, error, serviceHref) => [{ type: "BrowserRegions", id: serviceHref }],
    }),

    getTypes: builder.query<BrowserLink[], string>({
      queryFn: async (regionHref) => {
        try {
          const { request } = await import("../../services/cpln");
          let types: BrowserLink[] = [];
          let nextLink: string | undefined = regionHref;

          while (nextLink) {
            const { data } = await request<BrowserRes>({
              service: "browser",
              url: nextLink,
            });
            const pageTypes = data.links
              .filter((l) => l.rel === BROWSER_REL.type)
              .map((t) => ({
                _type: "link" as const,
                rel: t.rel,
                label: t.label || "",
                href: t.href,
              }));
            types = types.concat(pageTypes);
            nextLink = data.links.find((l) => l.rel === BROWSER_REL.next)?.href;
          }

          return { data: types };
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status || 500,
              data: error.response?.data || error.message,
            },
          };
        }
      },
      providesTags: (result, error, regionHref) => [{ type: "BrowserTypes", id: regionHref }],
    }),

    getDataItems: builder.query<{ label: string; items: BrowserData[] }, string>({
      queryFn: async (typeHref) => {
        try {
          const { request } = await import("../../services/cpln");
          let dataItems: BrowserData[] = [];
          let nextLink: string | undefined = typeHref;
          let label = "";

          while (nextLink) {
            const { data } = await request<BrowserRes>({
              service: "browser",
              url: nextLink,
            });
            if (data.data) {
              const pageItems = data.data.map((d) => ({
                _type: "data" as const,
                _cpln: {
                  hasChildren: d._cpln.hasChildren,
                  href: d._cpln.href,
                  label: d._cpln.label,
                  ref: d._cpln.ref,
                  mountUri: d._cpln.mountUri,
                },
              }));
              dataItems = dataItems.concat(pageItems);
            }
            // Extract label from type href
            if (!label) {
              const typeLink = data.links.find((l) => l.rel === BROWSER_REL.self);
              label = typeLink?.label || "";
            }
            nextLink = data.links.find((l) => l.rel === BROWSER_REL.next)?.href;
          }

          return { data: { label, items: dataItems } };
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status || 500,
              data: error.response?.data || error.message,
            },
          };
        }
      },
      providesTags: (result, error, typeHref) => [{ type: "BrowserItems", id: typeHref }],
    }),

    getChildren: builder.query<BrowserLink[], string>({
      queryFn: async (itemHref) => {
        try {
          const { request } = await import("../../services/cpln");
          const { data } = await request<BrowserRes>({
            service: "browser",
            url: itemHref,
          });

          const children = data.links
            .filter((l) => l.rel === BROWSER_REL.child)
            .map((c) => ({
              _type: "link" as const,
              rel: c.rel,
              label: c.label || "",
              href: c.href,
            }));

          return { data: children };
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status || 500,
              data: error.response?.data || error.message,
            },
          };
        }
      },
      providesTags: (result, error, itemHref) => [{ type: "BrowserChildren", id: itemHref }],
    }),

    getNestedItems: builder.query<BrowserData[], string>({
      queryFn: async (childHref) => {
        try {
          const { request } = await import("../../services/cpln");
          let dataItems: BrowserData[] = [];
          let nextLink: string | undefined = childHref;

          while (nextLink) {
            const { data } = await request<BrowserRes>({
              service: "browser",
              url: nextLink,
            });
            if (data.data) {
              const pageItems = data.data.map((d) => ({
                _type: "data" as const,
                _cpln: {
                  hasChildren: d._cpln.hasChildren,
                  href: d._cpln.href,
                  label: d._cpln.label,
                  ref: d._cpln.ref,
                  mountUri: d._cpln.mountUri,
                },
              }));
              dataItems = dataItems.concat(pageItems);
            }
            nextLink = data.links.find((l) => l.rel === BROWSER_REL.next)?.href;
          }

          return { data: dataItems };
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status || 500,
              data: error.response?.data || error.message,
            },
          };
        }
      },
      providesTags: (result, error, childHref) => [{ type: "BrowserItems", id: childHref }],
    }),
  }),
});

export const {
  useGetServicesQuery,
  useGetRegionsQuery,
  useGetTypesQuery,
  useGetDataItemsQuery,
  useGetChildrenQuery,
  useGetNestedItemsQuery,
} = browserApi;
